import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import ClientTabs from "./ClientTabs";
import { useActiveClients } from "../../hooks/useActiveClients";
import DocViewer from "../../components/DocViewer";
import { FeedbackThread } from "../../components/ConversationThread";
import { ChevronDown, MessageSquare, FileText, UserX, Eye, Volume2, DollarSign, HelpCircle, Check } from "lucide-react";
import { toast } from "sonner";

function MarkReadButton({ f, onRead }) {
  if (f.admin_read) return null;
  return (
    <span className="flex items-center gap-1.5">
      <span data-testid={`feedback-new-${f.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
        style={{ backgroundColor: "#F97316", color: "#fff" }}>New</span>
      <button data-testid={`feedback-mark-read-${f.id}`} onClick={() => onRead(f)}
        className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg border text-[#6B7280] hover:text-[#F97316] hover:border-[#F97316]"
        style={{ borderColor: "#E5E7EB" }}>
        <Check size={12} /> Mark read
      </button>
    </span>
  );
}

function ThreadBlock({ f, onReload }) {
  const send = async (text) => {
    await api.post(`/feedback/${f.id}/replies`, { text });
    toast.success("Reply sent — it will show in the Review portal");
    onReload();
  };
  return <FeedbackThread replies={f.replies} onSend={send} testPrefix={`feedback-${f.id}`} />;
}

function VoiceNote({ fileId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    api.get(`/files/${fileId}`, { responseType: "blob" }).then(({ data }) => {
      if (cancelled) return;
      url = URL.createObjectURL(data);
      setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return null;
  return <audio controls src={src} className="h-8" data-testid={`feedback-voice-${fileId}`} />;
}

function sourceSuffix(f) {
  return f.source_role === "worker" ? " (Team Portal)" : "";
}

function FeedbackItem({ f, onView, onAddToAccount, onRead, onReload }) {
  return (
    <div className="px-5 py-4 border-b last:border-0" style={{ borderColor: "#F3F4F6" }} data-testid={`feedback-item-${f.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold flex items-center gap-1.5">
          {f.anonymous
            ? <><UserX size={14} className="text-[#6B7280]" /> Anonymous{sourceSuffix(f)}</>
            : <>{f.name}{sourceSuffix(f)}</>}
          {f.post_window && (
            <span data-testid={`post-window-badge-${f.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>
              Post-window
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <MarkReadButton f={f} onRead={onRead} />
          <span className="text-xs text-[#6B7280]">{new Date(f.created_at).toLocaleString()}</span>
        </span>
      </div>
      {f.feedback_text && <p className="text-sm text-[#1A1A1A] leading-relaxed">{f.feedback_text}</p>}
      <div className="flex flex-wrap gap-2 mt-2">
        {f.post_window && f.source_role !== "worker" && (
          <button data-testid={`add-to-account-${f.id}`} onClick={() => onAddToAccount(f)}
            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>
            <DollarSign size={13} /> Add to Billing
          </button>
        )}
        {f.voice_file && (
          <VoiceNote fileId={f.voice_file.file_id} />
        )}
        {(f.attachments || []).map((a) => (
          <button key={a.id} onClick={() => onView({ file: a, title: a.filename })}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border text-[#6B7280] hover:text-[#F97316] hover:border-[#F97316]" style={{ borderColor: "#E5E7EB" }}>
            <Eye size={13} /> {a.filename}
          </button>
        ))}
      </div>
      <ThreadBlock f={f} onReload={onReload} />
    </div>
  );
}

function Group({ title, items, onView, general, onAddToAccount, onRead, onReload }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5"
        data-testid={`feedback-group-${general ? "general" : title}`}>
        <span className="font-heading font-semibold flex items-center gap-2">
          {general ? <MessageSquare size={16} style={{ color: "#F97316" }} /> : <FileText size={16} style={{ color: "#F97316" }} />}
          {title}
          <span className="text-xs font-normal text-[#6B7280]">({items.length})</span>
        </span>
        <ChevronDown size={18} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t" style={{ borderColor: "#E5E7EB" }}>
          {items.map((f) => (
            <FeedbackItem key={f.id} f={f} onView={onView} onAddToAccount={onAddToAccount} onRead={onRead} onReload={onReload} />
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionsSection({ items, onRead, onReload }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }} data-testid="worker-questions-section">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5"
        data-testid="worker-questions-toggle">
        <span className="font-heading font-semibold flex items-center gap-2">
          <HelpCircle size={16} style={{ color: "#F97316" }} />
          Worker & Client Questions
          <span className="text-xs font-normal text-[#6B7280]">({items.length})</span>
        </span>
        <ChevronDown size={18} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t" style={{ borderColor: "#E5E7EB" }}>
          {items.map((f) => (
            <div key={f.id} className="px-5 py-4 border-b last:border-0" style={{ borderColor: "#F3F4F6" }} data-testid={`question-item-${f.id}`}>
              <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                <span className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "#E8EDF4", color: "#0A1F44" }}>Question</span>
                  {f.document_id
                    ? <span className="text-xs font-medium text-[#6B7280] flex items-center gap-1"><FileText size={12} /> {f.document_title}</span>
                    : <span className="text-xs font-medium text-[#6B7280]">General Question / Feedback</span>}
                </span>
                <span className="flex items-center gap-2">
                  <MarkReadButton f={f} onRead={onRead} />
                  <span className="text-xs text-[#6B7280]">{new Date(f.created_at).toLocaleString()}</span>
                </span>
              </div>
              {f.feedback_text && <p className="text-sm text-[#1A1A1A] leading-relaxed">{f.feedback_text}</p>}
              <ThreadBlock f={f} onReload={onReload} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function groupFeedback(feedback) {
  const grouped = {};
  const general = [];
  const questions = [];
  feedback.forEach((f) => {
    if (f.is_question && f.source_role !== "worker") {
      questions.push(f);
    } else if (f.document_id) {
      const key = f.document_title || "Document";
      (grouped[key] = grouped[key] || []).push(f);
    } else {
      general.push(f);
    }
  });
  return { grouped, general, questions };
}

export default function AdminFeedback() {
  const { clients, active, setActive, status: clientsStatus, error: clientsError, retry: retryClients } = useActiveClients();
  const [feedback, setFeedback] = useState([]);
  const [viewing, setViewing] = useState(null);

  const loadFeedback = useCallback(() => {
    if (active) api.get(`/clients/${active}/feedback`).then(({ data }) => setFeedback(data));
  }, [active]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const markRead = async (f) => {
    try {
      await api.post(`/feedback/${f.id}/read`);
      toast.success("Marked as read");
      loadFeedback();
    } catch {
      toast.error("Could not mark as read");
    }
  };

  const addToAccount = async (f) => {
    try {
      await api.post(`/clients/${active}/account/from-feedback/${f.id}`);
      toast.success("Added to billing as a requested variation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to billing");
    }
  };

  const { grouped, general, questions } = groupFeedback(feedback);

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Feedback</h1>
      <p className="text-[#6B7280] text-sm mb-6">Client feedback grouped by document.</p>

      <ClientTabs clients={clients} active={active} onChange={setActive} status={clientsStatus} error={clientsError} onRetry={retryClients} />

      {active && (
        <div className="space-y-4">
          {feedback.length === 0 && (
            <div className="text-center py-16 text-[#6B7280]"><MessageSquare size={32} className="mx-auto mb-2 opacity-40" />No feedback yet.</div>
          )}
          {Object.entries(grouped).map(([title, items]) => (
            <Group key={title} title={title} items={items} onView={setViewing} onAddToAccount={addToAccount} onRead={markRead} onReload={loadFeedback} />
          ))}
          {general.length > 0 && <Group title="General Feedback" items={general} onView={setViewing} onAddToAccount={addToAccount} onRead={markRead} onReload={loadFeedback} general />}
          {questions.length > 0 && <QuestionsSection items={questions} onRead={markRead} onReload={loadFeedback} />}
        </div>
      )}

      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload onClose={() => setViewing(null)} />}
    </div>
  );
}
